import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, FormField, Input, Textarea, useToast } from '@vd/ui';
import { DestructiveConfirm } from '../../../features/modals/DestructiveConfirm';
import { useAuth, useCurrentCompany } from '../../../features/auth/AuthProvider';
import { api } from '../../../lib/api';
import styles from '../Settings.module.css';
import { WorkspaceMarkField } from '../../../features/workspaces/WorkspaceMarkField';

export function GeneralCompanyTab() {
  const current = useCurrentCompany();
  const { setCurrentCompany, companies } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const companyId = current?.id;
  const isOwner = current?.role === 'owner';

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => api.getCompany(companyId!),
    enabled: !!companyId,
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (company) setName(company.name);
  }, [company?.name]);

  if (!companyId || !company) return null;

  const dirty = name !== company.name;
  const save = async () => {
    setSaving(true);
    try {
      await api.updateCompany(companyId, { name });
      await qc.invalidateQueries({ queryKey: ['company', companyId] });
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast.push({ variant: 'success', title: 'Changes saved' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const destroy = async () => {
    try {
      await api.deleteCompany(companyId);
      toast.push({ variant: 'success', title: 'Company deleted', description: 'Its projects and data are in the 30-day purge window.' });
      const next = companies.find((c) => c.id !== companyId);
      if (next) {
        setCurrentCompany(next.id);
        navigate('/');
      } else {
        navigate('/signin');
      }
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Delete failed', description: (e as Error).message });
    }
  };

  return (
    <>
      <h1 className={styles.h1}>General</h1>
      <p className={styles.sub}>Company identity and basic settings.</p>
      <section className={styles.section}>
        <FormField label="Company name">
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isOwner} />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField label="Description" help="Shown on invites and admin surfaces. (Not yet persisted.)">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={!isOwner} />
        </FormField>
        <div style={{ height: 16 }} />
        <Button variant="primary" disabled={!dirty || !name.trim() || saving || !isOwner} loading={saving} onClick={save}>
          Save changes
        </Button>
        <div style={{ height: 20 }} />
        <WorkspaceMarkField company={company} canEdit={isOwner} />
        {!isOwner && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 8 }}>
            Only owners can edit company settings.
          </p>
        )}
      </section>
      {isOwner && (
        <section className={styles.section} style={{ borderColor: 'var(--danger-border)' }}>
          <h3 style={{ fontSize: 16, color: 'var(--danger)', marginBottom: 8 }}>Danger zone</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
            Deleting <strong>{company.name}</strong> tears the workspace
            down for everyone. Here's exactly what happens:
          </p>
          <ul style={{
            color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6,
            paddingLeft: 18, margin: '0 0 12px',
          }}>
            <li>
              <strong>Tickets, projects, comments, captures</strong> are
              soft-deleted immediately. Members can no longer see them.
              They're hard-purged after a 30-day grace window.
            </li>
            <li>
              <strong>All members</strong> lose access on their next
              page load. Their accounts stay; the company workspace is
              just gone from their company list.
            </li>
            <li>
              <strong>Any active Stripe subscription is canceled
              immediately.</strong> No further charges. Stripe prorates
              and refunds the unused portion automatically.
            </li>
            <li>
              <strong>MCP tokens scoped to this company stop working
              right away.</strong> Tokens scoped to multiple companies
              keep working for the others.
            </li>
            <li>
              <strong>Audit log entries</strong> are kept for the
              standard retention window (1 year).
            </li>
          </ul>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            After deletion, you'll be redirected to your next available
            company workspace. If this is your only company, you'll be
            sent to sign-in.
          </p>
          <Button variant="destructive" onClick={() => setDeleteOpen(true)}>Delete company…</Button>
        </section>
      )}

      <DestructiveConfirm
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={destroy}
        title={`Delete ${company.name}?`}
        description={`Tickets, projects, members, captures, and any active Stripe subscription will all be torn down. After deletion you'll be redirected to your next company (or sign-in if this is your only one). Soft-deleted data is hard-purged after a 30-day grace.`}
        typeToConfirm="DELETE"
        confirmLabel="Delete company"
      />
    </>
  );
}
